import { CaretDownOutlined, DeleteOutlined } from "@ant-design/icons";
import {
	Button,
	Checkbox,
	DatePicker,
	Form,
	Input,
	Modal,
	Radio,
	Select,
	Space,
	Tabs,
} from "antd";
import { FormInstance } from "antd/es";
import { GOTO_OPTIONS, OPTIONS } from "constants/survey";
import { useImperativeHandle, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { selectLang } from "store/lang/langSlice";
import { translate } from "utils";
import { Question } from "../../type";
interface Props {
	form: FormInstance;
	_ref: React.RefObject<QuestionConfigRefProps | null>;
}

export interface QuestionConfigRefProps {
	showErrorTab: () => void;
}

export const isSingleChoiceQuestion = (question: Question) =>
	question?.type === OPTIONS[0].value;

export default function QuestionConfig({ form, _ref }: Props) {
	const language = useSelector(selectLang);
	const [questionType, setQuestionType] = useState("");
	const [isShowSelectQuestionTypeModal, setIsShowSelectQuestionTypeModal] =
		useState(false);
	const [isShowSettingGotoModal, setIsShowSettingGotoModal] = useState(false);
	const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
	const [currentIndexQuestion, setCurrentIndexQuestion] = useState<
		number | null
	>(null);
	const questions: Question[] = Form.useWatch("questions", form);
	const [questionTabs, setQuestionTabs] = useState<string[]>([]);
	const dropdownGotoOptionsRef = useRef<any>();

	useImperativeHandle(_ref, () => ({
		showErrorTab: showErrorTab,
	}));

	const handleAddQuestion = () => {
		setIsShowSelectQuestionTypeModal(false);
		const addBtn = document.getElementById("add-question-btn");
		if (addBtn) {
			addBtn.click();
		}
	};

	const handleCloseQuestion = () => {
		setQuestionType("");
		setIsShowSelectQuestionTypeModal(false);
	};

	const handleDeleteQuestion = (remove: Function, name: number) => {
		window.dialogConfirm(
			translate("Confirmation"),
			translate("Do you want to delete this question?"),
			(isConfirm: boolean) => {
				if (isConfirm) {
					remove(name);
					const newQuestionTabs = questionTabs.filter(
						(_, index) => index === name,
					);
					setQuestionTabs(newQuestionTabs);
				}
			},
		);
	};

	const handleShowSettingGotoModal = (name: number) => {
		const question = questions[name];
		if (!question) return;
		const errorEle = document.getElementById("error_q_" + name);

		if (
			question.type === OPTIONS[0].value ||
			question.type === OPTIONS[1].value
		) {
			if (question.options) {
				for (let index = 0; index < question.options.length; index++) {
					if (
						!question.options[index] ||
						!question.options[index].content[language]
					) {
						if (errorEle) {
							errorEle.innerText = translate(
								"All options need to be completed",
							);
						}
						return;
					}
				}
			}
		}
		if (errorEle) {
			errorEle.innerText = "";
		}
		setCurrentQuestion(questions[name]);
		setCurrentIndexQuestion(name);
		setIsShowSettingGotoModal(true);
	};

	const renderQuestion = (
		lang: string,
		key: number,
		name: number,
		restField: any,
	) => {
		return (
			<>
				<div>
					<span>{translate("Question")}</span>
					<Form.Item
						{...restField}
						name={[name, "content", lang]}
						rules={[
							{
								required: true,
								message: translate("This field is required"),
							},
						]}
					>
						<Input />
					</Form.Item>
				</div>
				{(questions[name]?.type === OPTIONS[0].value ||
					questions[name]?.type === OPTIONS[1].value) && (
					<div>
						<Form.List
							name={[name, "options"]}
							initialValue={[
								{
									content: {
										en: translate('option'),
										km: translate('option'),
										zh: translate('option'),
									},
								},
								{
									content: {
										en: translate('option') + 2,
										km: translate('option') + 2,
										zh: translate('option') + 2,
									},
								},
							]}
						>
							{(fields, { add, remove }) => (
								<>
									<div className="mt-4 capitalize">{translate("options")}</div>

									{fields.map(({ key, name: fieldName, ...restField }) => {
										return (
											<Space key={key} className="w-full !block">
												<div className="mt-4 flex items-center gap-2">
													{questions[name]?.type === OPTIONS[0].value && (
														<Radio disabled></Radio>
													)}
													{questions[name]?.type === OPTIONS[1].value && (
														<Checkbox disabled></Checkbox>
													)}

													<Form.Item
														className="!block w-[calc(100%-100px)]"
														{...restField}
														name={[fieldName, "content", lang]}
														rules={[
															{
																required: true,
																message: translate("This field is required"),
															},
														]}
													>
														<Input className="w-full" />
													</Form.Item>

													<Button type="link" onClick={() => remove(fieldName)}>
														<DeleteOutlined className="text-[#b2bec3] hover:text-[#ff4d4f]" />
													</Button>
												</div>
											</Space>
										);
									})}

									<div className="mt-4 w-full">
										<Form.Item className="!w-fit">
											<Button onClick={() => add()} block>
												+ {translate("Add option")}
											</Button>
										</Form.Item>
									</div>
								</>
							)}
						</Form.List>
					</div>
				)}

				{questions[name]?.type === OPTIONS[2].value && (
					<div className="w-full mt-4">
						<span>{translate("Answer")}</span>
						<div className="ant-form-item">
							<textarea className="w-1/2" disabled />
						</div>
					</div>
				)}
				{questions[name]?.type === OPTIONS[3].value && (
					<div className="w-full mt-4">
						<div className="ant-form-item w-1/2">
							<DatePicker disabled placeholder="" className="w-full" />
						</div>
					</div>
				)}
			</>
		);
	};

	const hideDropdownOptions = () => {
		if (dropdownGotoOptionsRef.current) {
			dropdownGotoOptionsRef.current.focus();
			dropdownGotoOptionsRef.current.blur();
		}
	};

	const isSingleChoiceQuestion = (question: Question) =>
		question?.type === OPTIONS[0].value;

	const onSelectOption = (index: number, value: string | number) => {
		if (currentQuestion) {
			let _question = currentQuestion;
			if (isSingleChoiceQuestion(currentQuestion)) {
				const options = currentQuestion.options!.map((option, __index) => {
					if (index === __index) {
						return {
							...option,
							goToOption: value.toString(),
						};
					}
					return option;
				});
				_question = {
					...currentQuestion,
					options,
				};
			} else {
				_question = {
					...currentQuestion,
					goToOption: value.toString(),
				};
			}
			setCurrentQuestion(_question);
		}

		hideDropdownOptions();
	};

	const handleUpdateGotoSetting = () => {
		const _questions = questions.map((question, index) => {
			if (index === currentIndexQuestion) {
				return currentQuestion;
			}
			return question;
		});
		form.setFieldValue("questions", _questions);
		setIsShowSettingGotoModal(false);
	};

	const getValidQuestions = () => {
		return (
			questions?.filter(
				(question: Question) => question?.content && question.content[language],
			) || []
		);
	};

	const renderGotoOptionDropdown = (index: number) => (
		<>
			{getValidQuestions().length > 1 && (
				<div className="">
					<div className="px-5">
						<span>{translate("Question")}:</span>
					</div>
					<div className="">
						{getValidQuestions()?.map((question: Question, _index: number) => {
							if (_index === currentIndexQuestion) return <div key={_index} />;
							return (
								<div
									key={_index}
									className="cursor-pointer hover:text-black hover:bg-[#F5F5F5] py-2 px-9 truncate"
									onClick={() => onSelectOption(index, `Q_${_index + 1}`)}
								>
									<span>
										{`[Q${_index + 1}] `}
										{question.content[language]}
									</span>
								</div>
							);
						})}
					</div>
				</div>
			)}
		</>
	);

	const renderGotoOptions = (index: number) => (
		<div className="py-3 text-[#000000D9] flex flex-col gap-1">
			<div
				className="cursor-pointer hover:text-black hover:bg-[#F5F5F5] py-2 px-5"
				onClick={() => onSelectOption(index, GOTO_OPTIONS[0].value)}
			>
				<span className="capitalize">{translate("no branching")}</span>
			</div>
			{renderGotoOptionDropdown(index)}
			<div
				className="cursor-pointer hover:text-black hover:bg-[#F5F5F5] py-2 px-5 truncate"
				onClick={() => onSelectOption(index, GOTO_OPTIONS[1].value)}
			>
				<span>{translate("Go to thank you page")}</span>
			</div>
		</div>
	);

	const getOPtions = () => {
		const questions = getValidQuestions().map((question, index) => ({
			label: translate(question.content[language]),
			value: `Q_${index + 1}`,
		}));
		const defaultFirstOption = {
			...GOTO_OPTIONS[0],
			label: translate(GOTO_OPTIONS[0].label),
		};
		const defaultLastOption = {
			...GOTO_OPTIONS[1],
			label: translate(GOTO_OPTIONS[1].label),
		};
		return [defaultFirstOption, ...questions, defaultLastOption];
	};

	const renderCustomSelect = (value: string | number, index: number) => {
		return (
			<Select
				suffixIcon={<CaretDownOutlined className="!pointer-events-none" />}
				className="min-w-[100%] [&>div]:!border-0 [&>div]:!shadow-none [&>span]:!text-black max-w-[100%] truncate"
				value={value || GOTO_OPTIONS[0].value}
				dropdownRender={() => renderGotoOptions(index!)}
				placeholder={translate("Select option")}
				ref={dropdownGotoOptionsRef}
				options={getOPtions()}
			/>
		);
	};

	const showErrorTab = () => {
		form
			.validateFields()
			.then()
			.catch(error => {
				for (let index = 0; index < questions.length; index++) {
					const questionEle = document.getElementById("error_q_" + index);
					if (questionEle) {
						questionEle.innerText = "";
					}
				}
				const questionIndexs: number[] = [];
				const errorQuestions = error.errorFields.filter((field: any) => {
					if (field.name[0] === "questions") {
						if (questionIndexs.includes(field.name[1])) {
							return false;
						}
						questionIndexs.push(field.name[1]);
						return true;
					}
				});
				const newQuestionTabs = [...questionTabs];
				errorQuestions.forEach((question: any) => {
					newQuestionTabs[question.name[1]] =
						typeof question.name[3] === "string"
							? question.name[3]
							: question.name[5];
				});
				setQuestionTabs(newQuestionTabs);
			});
	};

	return (
		<div className="shadow-lg px-5 pt-5 pb-10 mb-10 bg-white p-5">
			<p className="font-semibold text-lg mb-4 capitalize">
				{translate(`questions`)}
			</p>
			<div className="mt-4 flex flex-col gap-4 relative">
				<Form.List name="questions">
					{(fields, { add, remove }) => (
						<>
							{questions &&
								fields.map(({ key, name, ...restField }) => {
									return (
										<Space key={key} className="w-full !block">
											<Form.Item
												initialValue={questionType}
												{...restField}
												name={[name, "type"]}
												className="hidden"
											/>

											<div className="shadow-md p-4 w-full">
												<div className="mb-4">
													<span className="text-lg font-bold">Q{name + 1}</span>
												</div>
												<div>
													<Tabs
														activeKey={questionTabs[name]}
														onChange={tab => {
															const newQuestionTabs = questionTabs.map(
																(_tab, index) => {
																	if (index === name) {
																		return tab;
																	}
																	return _tab;
																},
															);
															setQuestionTabs(newQuestionTabs);
														}}
													>
														<Tabs.TabPane
															forceRender
															tab={translate("English")}
															key="en"
														>
															{renderQuestion("en", key, name, restField)}
														</Tabs.TabPane>
														<Tabs.TabPane
															forceRender
															tab={translate("Chinese")}
															key="zh"
														>
															{renderQuestion("zh", key, name, restField)}
														</Tabs.TabPane>
														<Tabs.TabPane
															forceRender
															tab={translate("Cambodia")}
															key="km"
														>
															{renderQuestion("km", key, name, restField)}
														</Tabs.TabPane>
													</Tabs>
												</div>

												<div className="mt-4 py-2 px-4 flex justify-between border-t">
													<div>
														<span
															className="text-[#ff4d4f]"
															id={`error_q_${name}`}
														></span>
													</div>
													<div className="flex gap-6">
														<div className="px-6 border-r">
															<Button
																type="link"
																onClick={() =>
																	handleDeleteQuestion(remove, name)
																}
															>
																<DeleteOutlined className="text-[#868686] hover:text-[#ff4d4f] text-xl" />
															</Button>
														</div>
														<div>
															<Button
																disabled={
																	!questions[name]?.content ||
																	!questions[name]?.content[language]
																}
																type="link"
																className="mt-1 text-[#4085D6]"
																onClick={() => {
																	handleShowSettingGotoModal(name);
																}}
															>
																{translate(
																	"Settings go to section based on answer",
																)}
															</Button>
														</div>
													</div>
												</div>
											</div>
										</Space>
									);
								})}
							<Button
								className="w-fit"
								onClick={() => {
									setQuestionType("");
									setIsShowSelectQuestionTypeModal(true);
								}}
							>
								+ {translate("Add question")}
							</Button>
							{/* Hidden field start */}
							<Form.Item className="hidden">
								<Button onClick={() => add()} id={"add-question-btn"} />
							</Form.Item>
							{/* Hidden field end */}
						</>
					)}
				</Form.List>
			</div>

			<Modal
				className={""}
				title={<span className="uppercase">{translate('select question type')}</span>}
				open={isShowSelectQuestionTypeModal}
				onOk={handleAddQuestion}
				onCancel={handleCloseQuestion}
				okText={translate(`Confirm`)}
				destroyOnClose
				okButtonProps={{
					disabled: !questionType,
					className: '!text-white hover:!bg-[#1890ff]'
				}}
			>
				<label htmlFor="cancelReason" className="block">
					{translate("Question type")}
				</label>
				<Select
					id="cancelReason"
					className="w-full"
					placeholder={translate("select question type")}
					onChange={value => {
						setQuestionType(value);
					}}
					allowClear
				>
					{OPTIONS.map((option, index) => (
						<Select.Option value={option.value} key={index}>
							{translate(option.label)}
						</Select.Option>
					))}
				</Select>
			</Modal>

			<Modal
				width={1000}
				title={<span className="uppercase">{translate("Settings")}</span>}
				open={isShowSettingGotoModal}
				destroyOnClose
				onOk={handleUpdateGotoSetting}
				onCancel={() => setIsShowSettingGotoModal(false)}
			>
				<p className="font-bold">
					<span>
						{translate("Question")}
						{": "}
					</span>
					<span>{currentQuestion?.content[language]}</span>
				</p>

				{isSingleChoiceQuestion(currentQuestion!) && (
					<div className="px-6 mt-4">
						<div className="px-2">
							<div className="bg-[#FAFAFA] border-b flex w-full py-2">
								<div className="w-1/2 px-4">
									<span className="font-bold">
										{translate("Answer option")}
									</span>
								</div>
								<div className="w-1/2 px-4 border-l">
									<span className="font-bold">
										{translate("If selected, jump to question")}
									</span>
								</div>
							</div>
						</div>
						{currentQuestion?.options?.map((option, index) => (
							<div className="px-2" key={index}>
								<div className="border-b flex w-full py-2">
									<div className="w-1/2 px-4">
										<span className="">{option?.content[language] || ""}</span>
									</div>
									<div className="w-1/2 px-4">
										{renderCustomSelect(option.goToOption, index)}
									</div>
								</div>
							</div>
						))}
					</div>
				)}

				{!isSingleChoiceQuestion(currentQuestion!) && (
					<>
						<div className="px-6 mt-4">
							<div className="px-2">
								<div className="bg-[#FAFAFA] border-b flex w-full py-2">
									<div className="w-1/2 px-4">
										<span className="font-bold">
											{translate("If selected, jump to question")}
										</span>
									</div>
								</div>
							</div>
							<div className="px-2 w-full">
								{renderCustomSelect(
									currentQuestion?.goToOption ?? "",
									currentIndexQuestion!,
								)}
							</div>
						</div>
					</>
				)}
			</Modal>
		</div>
	);
}
